import { useEffect, useState } from "react";
import { Button, Collapse, Spin } from "antd";
import { useDropdowns } from "@utils/customHooks";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import "./filter.scss";
import { useChronicBreakdownStore } from "@stores/chronicBreakdownStore";
import { isEmpty, map } from "lodash";
import { MultiSelectOpen } from "../../qualityMeasureReport/filters/multiselect";
import memberDetailsService from "@services/memberDetails.service";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
import reportService from "@services/report.service";

const Filters = () => {
  const { loadingOptions, getOptions, filterDropdowns } = useDropdowns();
  const loadingCity = useChronicBreakdownStore((state) => state.loadingCity);
  const setLoadingCity = useChronicBreakdownStore((state) => state.setLoadingCity);
  const loadingHealthPlan = useChronicBreakdownStore((state) => state.loadingHealthPlan);
  const setLoadingHealthPlan = useChronicBreakdownStore((state) => state.setLoadingHealthPlan);
  const loadingPcpVendor = useChronicBreakdownStore((state) => state.loadingPcpVendor);
  const setLoadingPcpVendor = useChronicBreakdownStore((state) => state.setLoadingPcpVendor);
  const loadingProviders = useChronicBreakdownStore((state) => state.loadingProviders);
  const setLoadingProviders = useChronicBreakdownStore((state) => state.setLoadingProviders);
  const [flag, setFlag] = useState(false);
  const [hasInitialFilterRun, setHasInitialFilterRun] = useState(false);
  const prevAppliedFilters = useChronicBreakdownStore((state) => state.appliedProvider);
  const setPrevAppliedFilters = useChronicBreakdownStore((state) => state.setAppliedProvider);
  const selectedCity = useChronicBreakdownStore((state) => state.selectedCity);
  const setSelectedCity = useChronicBreakdownStore((state) => state.setSelectedCity);
  const selectedState = useChronicBreakdownStore((state) => state.selectedState);
  const setSelectedState = useChronicBreakdownStore((state) => state.setSelectedState);
  const selectedProvider = useChronicBreakdownStore((state) => state.selectedProvider);
  const setSelectedProvider = useChronicBreakdownStore((state) => state.setSelectedProvider);
  const selectedPCPVendor = useChronicBreakdownStore((state) => state.selectedPCPVendor);
  const setSelectedPcpVendor = useChronicBreakdownStore((state) => state.setSelectedPcpVendor);
  const selectedHealthPlan = useChronicBreakdownStore((state) => state.selectedHealthPlan);
  const setSelectedHealthPlan = useChronicBreakdownStore((state) => state.setSelectedHealthPlan);
  const stateOptions = useChronicBreakdownStore((state) => state.stateOptions);
  const setStateOptions = useChronicBreakdownStore((state) => state.setStateOptions);
  const cityOptions = useChronicBreakdownStore((state) => state.cityOptions);
  const setCityOptions = useChronicBreakdownStore((state) => state.setCityOptions);
  const providerOptions = useChronicBreakdownStore((state) => state.options);
  const setProviderOptions = useChronicBreakdownStore((state) => state.setOptions);
  const healthPlanOptions = useChronicBreakdownStore((state) => state.healthPlanOptions);
  const setHealthPlanOptions = useChronicBreakdownStore((state) => state.setHealthPlanOptions);
  const pcpVendorOptions = useChronicBreakdownStore((state) => state.pcpVendorOptions);
  const setPcpVendorOptions = useChronicBreakdownStore((state) => state.setPcpVendorOptions);
  const comingFromMembers = usePopulationInsightStore((state) => state.comingFromMembers);
  const handleComingFromMembersFlag = usePopulationInsightStore((state) => state.handleComingFromMembersFlag);
  const setChartData = useChronicBreakdownStore((state) => state.setChartData);
  const setChartLoading = useChronicBreakdownStore((state) => state.setChartLoading);
  const isChartLoading = useChronicBreakdownStore((state) => state.chartLoading);

  const fetchDropdownOptions = async ({
    attributeName,
    params = [],
    mapFn = (item: any) => ({ label: item.value, id: item.value }),
  }: {
    attributeName: string;
    params?: any[];
    mapFn?: (item: any) => any;
  }) => {
    try {
      const observable = memberDetailsService.fetchAttributeDropdownWithSqParams({
        attributeName,
        sqlParamsFromPayload: params,
      });

      return new Promise<any[]>((resolve, reject) => {
        observable.subscribe({
          next: (response: any) => {
            resolve((response?.data?.data?.data || []).map(mapFn));
          },
          error: (err: any) => {
            console.error(`Error fetching ${attributeName}`, err);
            reject(err);
          },
        });
      });
    } catch (error) {
      console.error(`Error fetching ${attributeName}`, error);
      return [];
    }
  };

  useEffect(() => {
    if (comingFromMembers) {
      setFlag(true);
    }
  }, [comingFromMembers]);

  // STATE
  useEffect(() => {
    getOptions("StateChronicReportFilter");
    setChartLoading(true);
  }, []);

  useEffect(() => {
    if (!comingFromMembers && filterDropdowns?.StateChronicReportFilter) {
      const updated = filterDropdowns.StateChronicReportFilter.map((item: any) => ({
        label: item.value,
        id: item.value,
      }));
      setStateOptions(updated);
      setSelectedState(updated.map((i: any) => i.id));
    }
  }, [filterDropdowns]);

  // CITY
  useEffect(() => {
    if (selectedState?.length) {
      (async () => {
        setLoadingCity(true);
        const result = await fetchDropdownOptions({
          attributeName: "CityChronicReportFilter",
          params: [selectedState],
        });
        setCityOptions(result);
        setSelectedCity(flag ? prevAppliedFilters[1] : result.map((i) => i.id));
        setLoadingCity(false);
      })();
    } else {
      setCityOptions([]);
      setSelectedCity([]);
    }
  }, [selectedState]);

  // HEALTH PLAN
  useEffect(() => {
    if (selectedCity?.length) {
      (async () => {
        setLoadingHealthPlan(true);
        const result = await fetchDropdownOptions({
          attributeName: "InsuranceChronicReportFilter",
          params: [selectedCity, selectedState],
        });
        setHealthPlanOptions(result);
        setSelectedHealthPlan(flag ? prevAppliedFilters[2] : result.map((i) => i.id));
        setLoadingHealthPlan(false);
      })();
    } else {
      setHealthPlanOptions([]);
      setSelectedHealthPlan([]);
    }
  }, [selectedCity]);

  // PCP VENDOR
  useEffect(() => {
    if (selectedHealthPlan?.length) {
      (async () => {
        setLoadingPcpVendor(true);
        const result = await fetchDropdownOptions({
          attributeName: "PCPVendorChronicReportFilter",
          params: [selectedHealthPlan, selectedCity, selectedState],
        });
        setPcpVendorOptions(result);
        setSelectedPcpVendor(flag ? prevAppliedFilters[3] : result.map((i) => i.id));
        setLoadingPcpVendor(false);
      })();
    } else {
      setPcpVendorOptions([]);
      setSelectedPcpVendor([]);
    }
  }, [selectedHealthPlan]);

  // PROVIDERS
  useEffect(() => {
    if (selectedPCPVendor?.length) {
      (async () => {
        setLoadingProviders(true);
        const result = await fetchDropdownOptions({
          attributeName: "PCPChronicReportFilter",
          params: [selectedPCPVendor, selectedHealthPlan, selectedCity, selectedState],
          mapFn: (item: any) => ({ label: item.value, id: item.key }),
        });
        result.push({ id: "NA", label: "NA" });
        setProviderOptions(result);
        setSelectedProvider(flag ? prevAppliedFilters[4] : result.map((i) => i.id));
        setLoadingProviders(false);
        setFlag(false);
      })();
    } else {
      setProviderOptions([]);
      setSelectedProvider([]);
    }
  }, [selectedPCPVendor]);

  useEffect(() => {
    const allFiltersAvailable = !isEmpty(selectedState) && !isEmpty(selectedCity) && !isEmpty(selectedHealthPlan) && !isEmpty(selectedPCPVendor) && !isEmpty(selectedProvider);
    const allDropdownsLoaded = !loadingCity && !loadingHealthPlan && !loadingPcpVendor && !loadingProviders;

    if (comingFromMembers && allDropdownsLoaded) {
      setSelectedState(prevAppliedFilters[0]);
      setSelectedCity(prevAppliedFilters[1]);
      setSelectedHealthPlan(prevAppliedFilters[2]);
      setSelectedPcpVendor(prevAppliedFilters[3]);
      setSelectedProvider(prevAppliedFilters[4]);

      handleApplyFilter(prevAppliedFilters[0], prevAppliedFilters[1], prevAppliedFilters[2], prevAppliedFilters[3], prevAppliedFilters[4]);

      setFlag(true);
      setHasInitialFilterRun(true);
      handleComingFromMembersFlag(false);
    } else if (allFiltersAvailable && !hasInitialFilterRun) {
      handleApplyFilter(selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider);
      setHasInitialFilterRun(true);
    }
  }, [comingFromMembers, hasInitialFilterRun, selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider]);

  const resetAllFilters = () => {
    setSelectedState([]);
    setSelectedCity([]);
    setSelectedHealthPlan([]);
    setSelectedPcpVendor([]);
    setSelectedProvider([]);
    setCityOptions([]);
    setHealthPlanOptions([]);
    setPcpVendorOptions([]);
    setProviderOptions([]);
  };

  useEffect(() => {
    return () => {
      resetAllFilters();
    };
  }, []);

  const handleSelectChange = (value: any) => {
    setSelectedProvider(value);
  };

  const { Panel } = Collapse;

  const handleApplyFilter = (selectedState: any, selectedCity: any, selectedHealthPlan: any, selectedPCPVendor: any, selectedProvider: any) => {
    setChartLoading(true);
    setPrevAppliedFilters([selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider]);
    reportService.getChronicBreakdownChartData(selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider).subscribe({
      next: (response: any) => {
        setChartData(response.data.data);
        setChartLoading(false);
      },
      error: (error) => {
        console.error(error);
        setChartLoading(false);
      },
      complete: () => setChartLoading(false),
    });
  };

  return (
    <Spin spinning={loadingCity || loadingHealthPlan || loadingOptions || loadingPcpVendor || loadingProviders} indicator={<HealthemSpinner />}>
      <div className="filter-section__panel mr-05">
        <Collapse className="filter-section__panel__collapse" expandIconPosition="end" defaultActiveKey={["1"]}>
          <Panel header="State" key="1">
            <MultiSelectOpen titleHeader="State" options={stateOptions} value={selectedState} handleSelect={setSelectedState} isSearchRequired={false} />
          </Panel>
          <Panel header="City" key="2">
            <MultiSelectOpen titleHeader="City" options={cityOptions} value={selectedCity} handleSelect={setSelectedCity} isSearchRequired={false} />
          </Panel>
          <Panel header="Health Plan" key="3">
            <MultiSelectOpen titleHeader="Health Plans" options={healthPlanOptions} value={selectedHealthPlan} handleSelect={setSelectedHealthPlan} isSearchRequired={false} />
          </Panel>
          <Panel header="PCP Vendor" key="4">
            <MultiSelectOpen titleHeader="PCP Vendor" options={pcpVendorOptions} value={selectedPCPVendor} handleSelect={setSelectedPcpVendor} isSearchRequired={false} />
          </Panel>
          <Panel header="PCP" key="5">
            <MultiSelectOpen titleHeader="PCP" options={providerOptions} value={selectedProvider} handleSelect={handleSelectChange} isSearchRequired={false} />
          </Panel>
        </Collapse>
        <div className="filter-section__actions mt-05">
          <Button
            type="primary"
            className="apply-filter"
            style={{ height: "40px" }}
            disabled={isEmpty(selectedProvider) || isChartLoading}
            onClick={() => {
              handleApplyFilter(selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider);
            }}
          >
            Apply Filter
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default Filters;